import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation36({ animation, badge, all_animations }: Props) {
  const [rotation, setRotation] = useState(0)
  const [unlocked, setUnlocked] = useState(false)

  useEffect(() => {
    // Animation de l'horloge qui tourne
    const rotateInterval = setInterval(() => {
      setRotation(prev => {
        if (prev >= 720) {
          clearInterval(rotateInterval)
          setTimeout(() => setUnlocked(true), 300)
          return 720
        }
        return prev + 30
      })
    }, 50)

    return () => clearInterval(rotateInterval)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes unlockPop {
          0% { transform: scale(0.8); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes tickTock {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }
        .unlock-pop {
          animation: unlockPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .tick-tock {
          animation: tickTock 0.5s ease-in-out infinite;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        <div className="relative">
          {/* Horloge / Cadran */}
          {!unlocked && (
            <div className={`w-32 h-32 sm:w-44 sm:h-44 rounded-full border-4 border-tessan-yellow bg-tessan-cream flex items-center justify-center ${rotation < 720 ? 'tick-tock' : ''}`}>
              {/* Centre de l'horloge */}
              <div className="absolute w-4 h-4 rounded-full bg-tessan-green" />

              {/* Aiguille des heures */}
              <div
                className="absolute w-1 h-12 bg-tessan-green origin-bottom"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  bottom: '50%'
                }}
              />

              {/* Aiguille des minutes */}
              <div
                className="absolute w-0.5 h-16 bg-tessan-green-hover origin-bottom"
                style={{
                  transform: `rotate(${rotation * 12}deg)`,
                  bottom: '50%'
                }}
              />

              {/* Marqueurs d'heure */}
              {Array.from({ length: 12 }, (_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-3 bg-tessan-green"
                  style={{
                    transform: `rotate(${i * 30}deg) translateY(-68px)`
                  }}
                />
              ))}
            </div>
          )}

          {/* Badge débloqué */}
          {unlocked && (
            <div className="unlock-pop">
              <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
                <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
              </div>
            </div>
          )}

          {/* Texte "débloqué" */}
          {!unlocked && (
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-tessan-yellow font-bold">
              Déverrouillage...
            </div>
          )}
        </div>

        <div className={`mt-12 text-center transition-all duration-700 ${unlocked ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
